// The Account section of Settings — one card for the whole identity story:
// your name, the account (org) name for owners, email, and password. This is
// the merger of the old "My Profile" and "Account Settings" tabs, which were
// two pages saying one thing (and both showed subscription info — that now
// lives only in Billing).

import { useState } from 'react';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Save, Loader2, KeyRound, User, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { deactivateSelf, SELF_REACTIVATE_DAYS } from '@/lib/deactivation';
import { markIntentionalSignOut } from '@/lib/errorLogger';

export default function AgentProfile() {
  const { user, profile, org, isOrgOwner, isSuperAdmin, isPlatformAdmin, refreshUserData, signOutEverywhere } = useAuth();
  const { isImpersonating, simulateProfileUpdate } = useImpersonation();
  const [name, setName] = useState(profile?.full_name ?? '');
  const [orgName, setOrgName] = useState(org?.name ?? '');
  const [saving, setSaving] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  // Deactivate my account (Drew, 2026-09-29): everyone but platform staff,
  // whom deactivate_self() refuses anyway. Signed out now; SELF_REACTIVATE_DAYS
  // to come back by signing in. An owner takes their organization with them.
  const canDeactivateSelf = !isSuperAdmin && !isPlatformAdmin;
  const deactivateTyped = !!user?.email && deactivateConfirm.trim().toLowerCase() === user.email.trim().toLowerCase();

  const handleDeactivateSelf = async () => {
    if (!deactivateTyped || isImpersonating) return;
    setDeactivating(true);
    try {
      await deactivateSelf();
      markIntentionalSignOut();
      toast.success('Account deactivated', { description: `Sign back in within ${SELF_REACTIVATE_DAYS} days to bring it back.` });
      setDeactivateOpen(false);
      // A deactivated account is out of every device, not just this one.
      await signOutEverywhere();
    } catch (err) {
      toast.error('Deactivation failed', { description: sanitizeError(err) });
    } finally {
      setDeactivating(false);
    }
  };

  const showOrgName = isOrgOwner && !!org;
  const nameChanged = !!name.trim() && name.trim() !== (profile?.full_name ?? '');
  const orgNameChanged = showOrgName && !!orgName.trim() && orgName.trim() !== org!.name;
  const anythingChanged = nameChanged || orgNameChanged;

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);

    // Impersonation: update the in-memory profile so the UI reflects the new
    // name without touching the real DB. safeSupabase would block the writes
    // anyway; this gives visible feedback.
    if (isImpersonating) {
      if (nameChanged) simulateProfileUpdate({ full_name: name.trim() });
      setSaving(false);
      toast.success('Account updated (sandbox)');
      return;
    }

    try {
      if (nameChanged) {
        const { error } = await supabase.from('profiles').update({ full_name: name.trim() }).eq('id', user.id);
        if (error) throw error;
      }
      if (orgNameChanged) {
        const { error } = await supabase.from('organizations').update({ name: orgName.trim() }).eq('id', org!.id);
        if (error) throw error;
      }
      toast.success('Account updated');
      await refreshUserData();
    } catch (error) {
      toast.error('Error', { description: sanitizeError(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setResettingPw(true);
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResettingPw(false);
    if (error) {
      toast.error('Error', { description: sanitizeError(error) });
    } else {
      toast.success('Password reset email sent', { description: 'Check your inbox for a reset link.' });
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Account
          </CardTitle>
          <CardDescription>Your name, sign-in, and account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Full Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} className="max-w-md" placeholder="Enter your name" />
          </div>

          {showOrgName && (
            <div className="space-y-2">
              <Label>Account Name</Label>
              <Input value={orgName} onChange={e => setOrgName(e.target.value)} className="max-w-md" placeholder="Your organization's name" />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-muted-foreground">Email</Label>
            <Input value={user?.email ?? ''} readOnly className="max-w-md bg-muted" />
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving || !anythingChanged}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleResetPassword} disabled={resettingPw}>
              {resettingPw ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <KeyRound className="h-4 w-4 mr-1" />}
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {canDeactivateSelf && (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserX className="h-4 w-4 text-destructive" />
              Deactivate my account
            </CardTitle>
            <CardDescription>
              You're signed out right away and disappear from every screen. Sign back in within {SELF_REACTIVATE_DAYS} days to bring your account back exactly as it was; after that, an administrator has to.
              {isOrgOwner && org ? ` You own ${org.name}, so it and everyone in it are deactivated with you and come back with you.` : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10"
              disabled={isImpersonating} title={isImpersonating ? 'Not available while viewing as someone else' : undefined}
              onClick={() => { setDeactivateConfirm(''); setDeactivateOpen(true); }}>
              <UserX className="h-4 w-4 mr-1" /> Deactivate my account
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={deactivateOpen} onOpenChange={o => { if (!o) setDeactivateOpen(false); }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Deactivate your account?</DialogTitle>
            <DialogDescription>
              You'll be signed out now. Your clients stay with your name, out of sight. Sign back in within {SELF_REACTIVATE_DAYS} days and everything comes back.
              {isOrgOwner && org ? ` ${org.name} and everyone in it go with you.` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="deactivate-self-confirm">Type your email to confirm</Label>
            <Input id="deactivate-self-confirm" value={deactivateConfirm} onChange={e => setDeactivateConfirm(e.target.value)}
              placeholder={user?.email ?? ''} className="font-mono text-sm" autoComplete="off" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeactivateSelf} disabled={!deactivateTyped || deactivating || isImpersonating}>
              {deactivating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserX className="h-4 w-4 mr-1" />}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
